import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";
import BackIcon from "../components/BackIcon";
import { PrimaryButton } from "../components/Button";
import OtpView from "../components/OtpView";
import { validateOtp, sendOtp, getUserProfile } from "../services/api";
import Navbar from "../components/Navbar";
import { useAuth } from "../contexts/AuthContext";

/* —— styled —— */
const Screen = styled.div`
  min-height: 100dvh;
  width: 100%;
  background: #fff;
  padding-top: max(80px, env(safe-area-inset-top));
  padding-bottom: max(80px, env(safe-area-inset-bottom));
`;

const Shell = styled.div`
  max-width: 400px;
  margin: 0 auto;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
`;

const HeaderBar = styled.header`
  height: 56px;
  display: flex;
  align-items: center;
  padding: 0 20px;
`;

const HeaderText = styled.h1`
  width: 100%;
  font-family: "Manrope", sans-serif;
  font-weight: 700;
  font-size: 20px;
  padding-left: 20px;
  margin-bottom: 6px;
`;

const CountdownRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 16px 20px 0;
`;

const Small = styled.p<{ $bold?: boolean }>`
  margin: 0;
  font-family: "Manrope", sans-serif;
  font-weight: ${(p) => (p.$bold ? 800 : 500)};
  font-size: 12px;
`;

const ResendText = styled.button`
  margin: 0;
  font-family: "Manrope", sans-serif;
  font-weight: 500;
  font-size: 12px;
  color: #D62422;
  background: none;
  border: none;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  
  &:disabled {
    color: #B1B1B1;
    text-decoration: none;
    cursor: not-allowed;
  }
`;

const BottomBar = styled.div`
  margin-top: auto;
  width: 100%;
  background: #ffffff;
  border-top: 1px solid #eee;
  padding: 16px 20px max(16px, env(safe-area-inset-bottom));
`;

/* —— types —— */
type Role = "student" | "trainer";

type LocationState = {
  countryCode?: string;
  mobileNumber?: string;
  role?: Role;
};

const isNonEmptyString = (value?: string | null) =>
  typeof value === "string" && value.trim().length > 0;

const toNumber = (value: unknown) => {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const hasBasicDetails1 = (userData: {
  name?: string | null;
  age?: number | string | null;
  sex?: string | null;
  experience?: string | null;
}) => {
  const ageNumber = toNumber(userData.age);
  return (
    isNonEmptyString(userData.name) &&
    typeof ageNumber === "number" &&
    ageNumber > 0 &&
    (userData.sex === "Male" || userData.sex === "Female") &&
    (userData.experience === "Beginner" ||
      userData.experience === "Intermediate" ||
      userData.experience === "Professional")
  );
};

const hasBasicDetails2 = (userData: {
  hasGloves?: boolean | null;
  gloveSize?: number | string | null;
  hasHandwrap?: boolean | null;
}) => {
  const gloveSizeNumber = toNumber(userData.gloveSize);
  return (
    typeof userData.hasGloves === "boolean" &&
    typeof userData.hasHandwrap === "boolean" &&
    typeof gloveSizeNumber === "number" &&
    gloveSizeNumber > 0
  );
};

interface Props {
  role?: Role;
}

/* —— component —— */
const OtpVerification: React.FC<Props> = ({ role = "student" }) => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const loc = useLocation();
  const { countryCode, mobileNumber, role: stateRole } =
    ((loc.state as LocationState) || {}) as LocationState;

  const currentRole: Role = stateRole || role;

  const OTP_LENGTH = 4;
  const [otp, setOtp] = useState<string[]>(
    Array.from({ length: OTP_LENGTH }, () => "")
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [isAutoVerifying, setIsAutoVerifying] = useState(false); // Ref to prevent double submission

  // Auto-verify when OTP is full
  useEffect(() => {
    const code = otp.join("");
    if (code.length === OTP_LENGTH && !isAutoVerifying && !loading) {
      // Dismiss keyboard
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      setIsAutoVerifying(true);
      handleVerifyOtp();
    }
  }, [otp]);

  // Reset auto-verifying flag when otp changes (if backspaced) or loading finishes
  useEffect(() => {
    if (otp.join("").length < OTP_LENGTH) {
      setIsAutoVerifying(false);
    }
  }, [otp]);

  useEffect(() => {
    const id = window.setInterval(
      () => setTimer((t) => {
        if (t > 0) {
          return t - 1;
        } else {
          setCanResend(true);
          return 0;
        }
      }),
      1000
    );
    return () => window.clearInterval(id);
  }, []);

  const clearError = () => setError("");

  const handleResendOtp = async () => {
    if (!canResend) return;

    setLoading(true);
    setError("");
    // Clear any already entered OTP
    setOtp(Array.from({ length: OTP_LENGTH }, () => ""));
    try {
      await sendOtp(countryCode, mobileNumber, currentRole);
      setTimer(30);
      setCanResend(false);
    } catch (err) {
      setError("Failed to resend OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const code = otp.join("");
    if (code.length !== OTP_LENGTH) {
      setError(`Please enter a ${OTP_LENGTH}-digit OTP`);
      return;
    }
    setLoading(true);
    try {
      const response = await validateOtp(countryCode, mobileNumber, code, currentRole);

      // Login user using AuthContext
      if (!response.accessToken) {
        throw new Error("No access token received from server");
      }

      const userId = response.user?._id || response.user?.id;

      login({
        role: currentRole,
        accessToken: response.accessToken,
        countryCode,
        mobileNumber,
        id: userId // Store user ID from response
      });

      // For students, check if basic details are already complete
      if (currentRole === "student" && userId) {
        try {
          const userProfile = await getUserProfile(userId);
          const userData = userProfile.user;

          const step1Done = hasBasicDetails1(userData);
          const step2Done = hasBasicDetails2(userData);

          if (step1Done && step2Done) {
            navigate("/student/home");
          } else if (step1Done && !step2Done) {
            navigate("/student/basic-details2");
          } else {
            navigate("/student/basic-details1");
          }
        } catch (profileError) {
          console.error("Error checking user profile:", profileError);
          // If profile check fails, default to onboarding
          navigate("/student/basic-details1");
        }
      } else {
        // For trainers or if no userId, navigate normally
        navigate(
          currentRole === "student" ? "/student/basic-details1" : "/trainer/dashboard"
        );
      }
    } catch {
      setError("Failed to verify OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100dvh',
        width: '100%',
        background: '#fff'
      }}
    >
      <div
        style={{
          maxWidth: '400px',
          margin: '0 auto',
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Navbar onBack={() => navigate(-1)} />

        {/* content (no scroll needed) */}
        <div className="pt-6">
          <HeaderText>Enter OTP sent to</HeaderText>
          <HeaderText>
            +{countryCode} {mobileNumber}
          </HeaderText>

          <OtpView otp={otp} setOtp={setOtp} error={error} clearError={clearError} />

          <CountdownRow>
            <Small>Didn't Receive?</Small>
            {canResend ? (
              <ResendText onClick={handleResendOtp} disabled={loading}>
                Resend OTP
              </ResendText>
            ) : (
              <Small $bold>{`00:${String(timer).padStart(2, "0")}`}</Small>
            )}
          </CountdownRow>

          {error && (
            <div className="px-5 pt-4">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}
        </div>

        {/* bottom button, anchored to bottom via flex */}
        <BottomBar>
          <PrimaryButton
            onClick={handleVerifyOtp}
            disabled={loading}
            label={loading ? "VERIFYING..." : "VERIFY"}
            className="!w-full"
          />
        </BottomBar>
      </div>
    </div>
  );
};

export default OtpVerification;
